import type {
  ProductSubscriptionBillingStat,
  ProductSubscriptionDTO,
} from '../productSubscription.types';
import type { RealtimeUsage } from '@repositories/usage/usage.types';
import { SoftwareSimpleModel } from '@models/softwareModels';

/**
 * 앱(상품) 단위 구독 집계 리스트 모델.
 * BE null/undefined 값에 대한 방어 처리를 getter 에서 수행.
 */
export class ProductSubscriptionListModel {
  private readonly _dto: ProductSubscriptionDTO;
  private readonly _software: SoftwareSimpleModel;

  constructor(dto: ProductSubscriptionDTO) {
    this._dto = dto;
    this._software = new SoftwareSimpleModel(dto.software);
  }

  get originDTO(): ProductSubscriptionDTO {
    return this._dto;
  }

  get softwareId(): string {
    return this._dto.softwareId;
  }

  get software(): SoftwareSimpleModel {
    return this._software;
  }

  get softwareName(): string {
    return this._software.name;
  }

  get softwareIconUrl(): string {
    return this._software.iconUrl;
  }

  get representativePlanName(): string {
    return this._dto.representativePlanName ?? '';
  }

  get planCount(): number {
    return this._dto.planCount ?? 0;
  }

  get representativeUserGroupName(): string {
    return this._dto.representativeUserGroupName ?? '';
  }

  get userGroupCount(): number {
    return this._dto.userGroupCount ?? 0;
  }

  /**
   * BE nextExpiryDate 는 null 이면 "무기한" 을 의미.
   * 렌더링 측에서 null 체크 후 무기한 문구로 표기.
   */
  get nextExpiryDate(): string | null {
    return this._dto.nextExpiryDate ?? null;
  }

  /** BE `subscriptionCount` — UI의 구독 건수 배지와 동일 */
  get subscriptionCount(): number {
    return this._dto.subscriptionCount ?? 0;
  }

  get optimizableLicenseCount(): number {
    return this._dto.optimizableLicenseCount ?? 0;
  }

  get usageWorkflowFailedCount(): number {
    return this._dto.usageWorkflowFailedCount ?? 0;
  }

  get totalSubscriptionCount(): number {
    return this.subscriptionCount;
  }

  /**
   * 연 비용 (includesBillingStat=true 일 때만 응답에 포함).
   */
  get billingStat(): ProductSubscriptionBillingStat | null {
    return this._dto.billingStat ?? null;
  }

  get annualPaymentEstimate(): number | null {
    return this._dto.billingStat?.annualPaymentEstimate ?? null;
  }

  get realtimeUsage(): RealtimeUsage | null {
    return this._dto.realtimeUsage ?? null;
  }
}
